// Detailed Overview — checklist + WO compliance dashboard modelled
// after the BD Technology Campus FM Operations Report. Lives directly
// under the existing KPI cards on /overview. Five panels driven off
// the same /api/v1/analytics/dashboard-detailed payload:
//
//   1. Filter bar           — date range, site, location, breakdown dim
//   2. Detailed KPI strip   — period totals + compliance % (WO + chk)
//   3. Daily compliance bars — two series side-by-side per day
//   4. Compliance heatmap   — calendar cells coloured by daily rate
//   5. Location breakdown   — bar list grouped by the chosen dimension
//   6. Missed-items table   — past-due open WOs + un-submitted checklists
//
// Everything reloads via React Query when a filter changes.

import { useMemo, useState } from 'react'
import {
  Alert, Box, Button, Chip, CircularProgress, MenuItem, Stack, TextField,
  Tooltip, Typography, ToggleButton, ToggleButtonGroup, alpha,
} from '@mui/material'
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined'
import Grid from '@mui/material/Grid2'
import { useQuery } from '@tanstack/react-query'
import type { DetailedDashboardData } from '@cmms/api-client'
import { api } from '../../lib/api'
import { SearchableSelect } from '../../components/SearchableSelect'
import type { Site, Location } from '@cmms/types'

import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined'
import ChecklistRtlOutlinedIcon from '@mui/icons-material/ChecklistRtlOutlined'
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined'
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import ViewModuleOutlinedIcon from '@mui/icons-material/ViewModuleOutlined'

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Return YYYY-MM-DD for the date `daysAgo` days before today. */
function isoDaysAgo(daysAgo: number): string {
  const d = new Date()
  d.setDate(d.getDate() - daysAgo)
  return d.toISOString().slice(0, 10)
}

function isoToday(): string {
  return new Date().toISOString().slice(0, 10)
}

/** Percentage (0-100, one decimal), 0 when there's nothing scheduled. */
function pct(num: number, den: number): number {
  return den > 0 ? Math.round((num / den) * 1000) / 10 : 0
}

/** Colour bucket for compliance %. Mirrors the BD report thresholds. */
function complianceColor(pct: number): string {
  if (pct >= 80) return '#10B981' // green
  if (pct >= 65) return '#F59E0B' // amber
  return '#EF4444'                // red
}

/** Fixed colours for the known `work_type` enum values, so the same
 *  type reads the same colour every time you look at the chart. An
 *  unrecognised value (custom/legacy data) falls back to a hash-based
 *  pick from the same palette used elsewhere in this file (roleColor). */
const WORK_TYPE_COLORS: Record<string, string> = {
  reactive: '#EF4444', preventive: '#10B981', inspection: '#0EA5E9',
  safety: '#F59E0B', other: '#94A3B8',
}
function workTypeColor(wt: string): string {
  if (WORK_TYPE_COLORS[wt]) return WORK_TYPE_COLORS[wt]
  const palette = ['#0D9488', '#7C3AED', '#2563EB', '#DB2777', '#F59E0B', '#0EA5E9']
  let h = 0
  for (let i = 0; i < wt.length; i++) h = ((h << 5) - h + wt.charCodeAt(i)) | 0
  return palette[Math.abs(h) % palette.length]!
}
function workTypeLabel(wt: string): string {
  return wt.length === 0 ? 'Other' : wt[0]!.toUpperCase() + wt.slice(1)
}

/** Short display label for a date like "2026-04-15" → "Apr 15". */
function shortDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/** Date label used in the HTML export's filter summary, e.g.
 *  "2026-04-15" → "15 Apr 2026". Locale-friendly + unambiguous. */
function formatHumanDate(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso + 'T00:00:00')
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-US', {
    day: 'numeric', month: 'short', year: 'numeric',
  })
}

// ─── Top-level component ────────────────────────────────────────────────────

export function DetailedOverview({
  // When set, the entire dashboard scopes server-side to the given
  // user as assignee + checklist submitter. Used by the My Overview
  // tab — leave undefined for the org-wide Detailed Overview view.
  userId,
  hidePeoplePanel,
  // Initial Type-filter value — lets a caller (e.g. Checklist Overview)
  // open scoped to a specific kind instead of the combined 'all' view.
  defaultItemType = 'all',
  // Restricts which Type options the filter bar offers at all, e.g.
  // Checklist Overview passes ['task', 'checklist'] so Work Orders (and
  // the combined 'all') never appear as choices — not just hidden by
  // default, unreachable. Leave undefined for every option (the
  // org-wide Detailed Overview view).
  allowedItemTypes,
}: {
  userId?: string
  hidePeoplePanel?: boolean
  defaultItemType?: ItemTypeFilter
  allowedItemTypes?: ItemTypeFilter[]
} = {}) {
  // Filter state — defaults: last 30 days, all sites, building dimension.
  const [from, setFrom] = useState(() => isoDaysAgo(29))
  const [to, setTo]     = useState(() => isoToday())
  const [siteId, setSiteId] = useState<string>('')
  const [locationId, setLocationId] = useState<string>('')
  const [dimension, setDimension] = useState<DetailedDashboardData['filters']['dimension']>('building')
  // Type filter — 'all' (default) is today's combined view. 'work_order'
  // / 'task' restrict every panel to that kind server-side (checklists
  // attached to the other kind drop out too, since they're only ever
  // counted for wos already in scope). 'checklist' doesn't restrict the
  // server query — it's a display-only mode: every panel forces its own
  // WO/Checklist toggle to checklist and hides the WO series entirely.
  const [itemType, setItemType] = useState<ItemTypeFilter>(defaultItemType)
  // Task Type filter — 'all' (default) is unrestricted. 'preventive' /
  // 'inspection' narrow every panel to that work_type server-side, same
  // append-after-kindClause mechanism as itemType (see
  // analytics.service.js's workTypeClause). The work_type column also
  // allows 'reactive'/'safety'/'other', but those aren't exposed here.
  const [workType, setWorkType] = useState<WorkTypeFilter>('all')
  // Tag filter — populated only when dimension === 'tags'. When
  // dimension flips away from 'tags' the picker hides but we keep
  // the ids in state so a quick flip back doesn't lose the selection.
  const [tagIds, setTagIds] = useState<string[]>([])
  // Role scoping is per-People-panel — server narrows the people array
  // to one role at a time. Kept at the top so the fetch URL stays
  // single-source.
  const [roleId, setRoleId] = useState<string>('')

  // Site + location pickers driven by existing list endpoints.
  const sitesQ = useQuery({
    queryKey: ['sites'],
    queryFn: () => api.sites.list(),
    staleTime: 5 * 60_000,
  })
  const sites: Site[] = Array.isArray(sitesQ.data) ? sitesQ.data : []
  const locationsQ = useQuery({
    queryKey: ['locations', siteId],
    queryFn: () => api.locations.list(siteId),
    enabled: !!siteId,
    staleTime: 60_000,
  })
  const locations: Location[] = Array.isArray(locationsQ.data) ? locationsQ.data : []

  // Main data fetch — re-runs whenever any filter changes. `roleId`
  // only scopes the People panel server-side; the rest of the
  // response is unaffected.
  // Only send tag_ids when the tag dimension is active — otherwise
  // the picker sits idle and we don't want its (possibly stale) ids
  // narrowing the dashboard invisibly.
  const activeTagIds = dimension === 'tags' ? tagIds : []
  // 'checklist' is a display-only mode (see itemType comment above) —
  // only 'work_order' / 'task' actually get sent to the server.
  const serverItemType = itemType === 'work_order' || itemType === 'task' ? itemType : undefined
  const serverWorkType = workType === 'all' ? undefined : workType
  const detailQ = useQuery({
    queryKey: ['analytics-detailed', { from, to, siteId, locationId, dimension, roleId, userId, tagIds: activeTagIds, itemType: serverItemType, workType: serverWorkType }],
    queryFn: () => api.analytics.dashboardDetailed({
      from, to,
      site_id: siteId || undefined,
      location_id: locationId || undefined,
      dimension,
      role_id: roleId || undefined,
      user_id: userId || undefined,
      tag_ids: activeTagIds.length > 0 ? activeTagIds : undefined,
      item_type: serverItemType,
      work_type: serverWorkType,
    }),
    staleTime: 30_000,
  })

  // Safe default response so the layout always renders panels — even
  // on a first load (before the network call completes) or when the
  // request errors. Each panel handles empty arrays / zero counts.
  const emptyData: DetailedDashboardData = {
    range: { from, to,
      days: Math.max(1, Math.round((new Date(to).getTime() - new Date(from).getTime()) / 86_400_000) + 1) },
    filters: { site_id: null, location_id: null, dimension, item_type: serverItemType ?? null, work_type: serverWorkType ?? null },
    kpis: {
      workOrders: { scheduled: 0, completed: 0, compliance: 0 },
      checklists: { scheduled: 0, submitted: 0, compliance: 0 },
      ongoing: 0,
      byKind: { work_order: { scheduled: 0, completed: 0 }, task: { scheduled: 0, completed: 0 } },
    },
    daily: [], breakdown: [], missed: [], people: [], roles: [],
    timeSlots: [
      { slot: 'morning',     label: 'Morning (5–9 AM)',     wo: { scheduled: 0, completed: 0, compliance: 0 }, checklist: { scheduled: 0, submitted: 0, compliance: 0 } },
      { slot: 'mid_morning', label: 'Mid-morning (10–12)',  wo: { scheduled: 0, completed: 0, compliance: 0 }, checklist: { scheduled: 0, submitted: 0, compliance: 0 } },
      { slot: 'afternoon',   label: 'Afternoon (1–3 PM)',   wo: { scheduled: 0, completed: 0, compliance: 0 }, checklist: { scheduled: 0, submitted: 0, compliance: 0 } },
      { slot: 'evening',     label: 'Evening (4–6 PM)',     wo: { scheduled: 0, completed: 0, compliance: 0 }, checklist: { scheduled: 0, submitted: 0, compliance: 0 } },
    ],
    observations: [],
  }
  const data: DetailedDashboardData = detailQ.data ?? emptyData
  // Dynamic label for every "WO" reference across the panels — when
  // scoped to one kind, the wo bucket in the response IS that kind's
  // data, so the label needs to say so rather than always reading "WO".
  const woLabel = itemType === 'task' ? 'Tasks' : 'Work orders'
  const woLabelShort = itemType === 'task' ? 'Task' : 'WO'
  const checklistOnly = itemType === 'checklist'
  // Mirror of checklistOnly for the opposite direction — "Work orders"
  // or "Tasks" selected, so checklists don't apply to any chart at all
  // (every panel drops its WO/Checklist toggle and shows only the WO
  // series, full width).
  const hideChecklist = itemType === 'work_order' || itemType === 'task'

  return (
    <Stack spacing={3}>
      {/* Filter bar — tab title + subtitle handled by the parent
          PageShell, so we jump straight into controls here. */}
      <FilterBar
        from={from} to={to}
        siteId={siteId} locationId={locationId}
        dimension={dimension}
        tagIds={tagIds}
        itemType={itemType}
        allowedItemTypes={allowedItemTypes}
        workType={workType}
        onFrom={setFrom} onTo={setTo}
        onSite={(v) => { setSiteId(v); setLocationId('') }}
        onLocation={setLocationId}
        onDimension={setDimension}
        onTagIds={setTagIds}
        onItemType={setItemType}
        onWorkType={setWorkType}
        sites={sites}
        locations={locations}
      />

      {/* Status banner — always rendered above the panels so the user
          knows whether they're looking at fresh data, a stale fetch,
          or an error. The panels themselves render unconditionally
          below with empty-state fallbacks. */}
      {detailQ.isError && (
        <Alert
          severity="error"
          action={
            <Button size="small" color="inherit"
              startIcon={<RefreshOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={() => detailQ.refetch()}>
              Retry
            </Button>
          }
          sx={{ fontSize: 13 }}
        >
          Could not load the detailed dashboard. The panels below show empty
          state until the next successful fetch.
          {detailQ.error instanceof Error && (
            <Box component="span" sx={{ display: 'block', mt: 0.5, opacity: 0.85, fontSize: 12 }}>
              {detailQ.error.message}
            </Box>
          )}
        </Alert>
      )}
      {detailQ.isLoading && !detailQ.data && (
        <Stack direction="row" alignItems="center" spacing={1.5}
          sx={{ color: 'text.secondary' }}>
          <CircularProgress size={16} />
          <Typography sx={{ fontSize: 12.5 }}>Loading detailed metrics…</Typography>
        </Stack>
      )}
      {detailQ.isFetching && detailQ.data && (
        <Stack direction="row" alignItems="center" spacing={1}
          sx={{ color: 'text.disabled', fontSize: 12 }}>
          <CircularProgress size={12} />
          <Typography sx={{ fontSize: 11.5 }}>Refreshing…</Typography>
        </Stack>
      )}

      <Stack spacing={3}>
        <KpiStrip data={data} woLabel={woLabel} checklistOnly={checklistOnly} hideChecklist={hideChecklist} />
        {/* Group-by-tag rollup — one card per tag showing the sites +
            locations assigned to it. Hides when the picker is active
            because the rest of the dashboard is already scoped to the
            selected tags. */}
        {dimension === 'tags' && tagIds.length === 0 && (
          <TagGroupsPanel sites={sites} />
        )}
        {dimension !== 'tags' && (
          <TagGroupsPanel sites={sites} />
        )}
        <DailyTrendPanel data={data} woLabelShort={woLabelShort} checklistOnly={checklistOnly}
          hideChecklist={hideChecklist} />
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 7 }}>
            <BreakdownPanel data={data} dimension={dimension}
              woLabelShort={woLabelShort} checklistOnly={checklistOnly} hideChecklist={hideChecklist} />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <HeatmapPanel data={data} checklistOnly={checklistOnly} hideChecklist={hideChecklist}
              woLabelShort={woLabelShort} />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          {!hidePeoplePanel && (
            <Grid size={{ xs: 12, md: 7 }}>
              <PeoplePanel data={data}
                roleId={roleId} onRoleId={setRoleId}
                woLabelShort={woLabelShort} checklistOnly={checklistOnly} hideChecklist={hideChecklist} />
            </Grid>
          )}
          <Grid size={{ xs: 12, md: hidePeoplePanel ? 12 : 5 }}>
            <TimeSlotsPanel data={data} woLabelShort={woLabelShort} checklistOnly={checklistOnly} hideChecklist={hideChecklist} />
          </Grid>
        </Grid>
        <MissedPanel data={data} itemType={itemType} />
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <ObservationsPanel data={data} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <GoodWorkPanel data={data} />
          </Grid>
        </Grid>
        <RecommendedActionsPanel data={data} />
      </Stack>
    </Stack>
  )
}

// ─── Filter bar ─────────────────────────────────────────────────────────────

export type ItemTypeFilter = 'all' | 'work_order' | 'task' | 'checklist'
export type WorkTypeFilter = 'all' | 'preventive' | 'inspection'

function FilterBar({
  from, to, siteId, locationId, dimension, tagIds, itemType, allowedItemTypes, workType,
  onFrom, onTo, onSite, onLocation, onDimension, onTagIds, onItemType, onWorkType,
  sites, locations,
}: {
  from: string; to: string
  siteId: string; locationId: string
  dimension: DetailedDashboardData['filters']['dimension']
  tagIds: string[]
  itemType: ItemTypeFilter
  allowedItemTypes?: ItemTypeFilter[]
  workType: WorkTypeFilter
  onFrom: (v: string) => void
  onTo: (v: string) => void
  onSite: (v: string) => void
  onLocation: (v: string) => void
  onDimension: (v: DetailedDashboardData['filters']['dimension']) => void
  onTagIds: (v: string[]) => void
  onItemType: (v: ItemTypeFilter) => void
  onWorkType: (v: WorkTypeFilter) => void
  sites: Site[]
  locations: Location[]
}) {
  // Tag registry — fetched only when the tag dimension is active so
  // the request doesn't fire for admins who never touch the picker.
  const tagsQ = useQuery({
    queryKey: ['tags'],
    queryFn: () => api.tags.list(),
    staleTime: 60_000,
    enabled: dimension === 'tags',
  })
  const allTags = Array.isArray(tagsQ.data) ? tagsQ.data : []
  const selectedTags = allTags
    .filter((t) => tagIds.includes(t.id))
    .map((t) => ({ id: t.id, name: t.name, color: t.color }))
  // Date-range quick presets — clicking one writes both from + to.
  const presets: Array<{ label: string; days: number }> = [
    { label: '7d',  days: 6  },
    { label: '30d', days: 29 },
    { label: '90d', days: 89 },
  ]
  // Which preset (if any) is currently active. Matches by computed
  // diff so toggling between 7d/30d/90d feels responsive.
  const activePreset = (() => {
    if (to !== isoToday()) return null
    const fromDate = new Date(from)
    const days = Math.round((Date.now() - fromDate.getTime()) / 86_400_000)
    return presets.find((p) => Math.abs(days - p.days) <= 1)?.label ?? null
  })()

  // Friendly labels for the dimension dropdown — reused by the
  // `data-filter-summary` payload below so the HTML export shows
  // the same words the user sees in the UI, not the raw key.
  const DIM_LABELS: Record<DetailedDashboardData['filters']['dimension'], string> = {
    building: 'Building', floor: 'Floor', area: 'Area',
    location: 'Location', location_type: 'Location type', site: 'Site',
    tags: 'Tags',
  }
  const TYPE_LABELS: Record<ItemTypeFilter, string> = {
    all: 'All', work_order: 'Work orders', task: 'Tasks', checklist: 'Checklists',
  }
  const WORK_TYPE_LABELS: Record<WorkTypeFilter, string> = {
    all: 'All', preventive: 'Preventive', inspection: 'Inspection',
  }

  // Friendly summary the HTML download serialiser reads off the DOM
  // at export time. Each entry becomes a chip in the report banner.
  // Stored as JSON in a data-attribute so the snapshot path stays
  // dependency-free — no React props on the download side, no DOM
  // querying heuristics inside the download component.
  const siteName = siteId
    ? (sites.find((s) => s.id === siteId)?.name ?? 'Site')
    : 'All sites'
  const locationName = locationId
    ? (locations.find((l) => l.id === locationId)?.name ?? 'Location')
    : (siteId ? 'All locations' : '—')
  const filterSummary: Array<{ label: string; value: string }> = [
    {
      label: 'Time period',
      value: `${formatHumanDate(from)} → ${formatHumanDate(to)}${activePreset ? ` (Last ${activePreset})` : ''}`,
    },
    { label: 'Site',     value: siteName     },
    { label: 'Location', value: locationName },
    { label: 'Group by', value: DIM_LABELS[dimension] },
    { label: 'Type',     value: TYPE_LABELS[itemType] },
    { label: 'Task Type', value: WORK_TYPE_LABELS[workType] },
  ]

  return (
    <Box
      data-filter-summary={JSON.stringify(filterSummary)}
      sx={{
      borderRadius: 2.5,
      bgcolor: 'background.paper',
      border: '1px solid', borderColor: 'divider',
      overflow: 'hidden',
      // Subtle accent strip at the top so the bar reads as a single
      // composed control, not a stack of disconnected inputs.
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: 'linear-gradient(90deg, #6366F1 0%, #0D9488 50%, #10B981 100%)',
      },
    }}>
      <Grid container sx={{ p: { xs: 2, md: 2.5 }, pt: { xs: 2.5, md: 3 } }} spacing={{ xs: 2, md: 3 }}>
        {/* ── Time period ── */}
        <Grid size={{ xs: 12, md: 5 }} sx={{ minWidth: 0 }}>
          <FilterSectionLabel icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 14 }} />}>
            Time period
          </FilterSectionLabel>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems="stretch">
            <TextField
              type="date" size="small"
              value={from}
              onChange={(e) => onFrom(e.target.value)}
              sx={dateInputSx}
            />
            <Box sx={{
              display: 'flex', alignItems: 'center',
              color: 'text.disabled', fontSize: 13, fontWeight: 600,
              px: 0.25,
            }}>→</Box>
            <TextField
              type="date" size="small"
              value={to}
              onChange={(e) => onTo(e.target.value)}
              sx={dateInputSx}
            />
          </Stack>
          <Stack direction="row" spacing={0.75} sx={{ mt: 1.25, flexWrap: 'wrap', gap: 0.75 }}
            useFlexGap>
            {presets.map((p) => {
              const active = activePreset === p.label
              return (
                <Box key={p.label}
                  role="button"
                  onClick={() => { onFrom(isoDaysAgo(p.days)); onTo(isoToday()) }}
                  sx={{
                    px: 1.25, py: 0.4,
                    borderRadius: 99,
                    fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: active ? 'primary.main' : 'divider',
                    bgcolor: active ? 'primary.main' : 'transparent',
                    color: active ? 'primary.contrastText' : 'text.secondary',
                    transition: 'all 150ms ease',
                    userSelect: 'none',
                    '&:hover': {
                      borderColor: 'primary.main',
                      color: active ? 'primary.contrastText' : 'primary.main',
                    },
                  }}>
                  Last {p.label}
                </Box>
              )
            })}
          </Stack>
        </Grid>

        {/* ── Scope ── */}
        <Grid size={{ xs: 12, md: 4 }} sx={{ minWidth: 0 }}>
          <FilterSectionLabel icon={<PlaceOutlinedIcon sx={{ fontSize: 14 }} />}>
            Scope
          </FilterSectionLabel>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <SearchableSelect
                value={siteId}
                onChange={onSite}
                options={[{ id: '', name: 'All sites' } as Site, ...sites]}
                getValue={(s) => s.id}
                getLabel={(s) => s.name}
                placeholder="Site"
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <SearchableSelect
                value={locationId}
                onChange={onLocation}
                options={[{ id: '', name: 'All locations' } as Location, ...locations]}
                getValue={(l) => l.id}
                getLabel={(l) => l.name}
                placeholder={siteId ? 'Location' : 'Pick site first'}
                disabled={!siteId}
              />
            </Box>
          </Stack>
        </Grid>

        {/* ── Grouping ── */}
        <Grid size={{ xs: 12, md: 3 }} sx={{ minWidth: 0 }}>
          <FilterSectionLabel icon={<ViewModuleOutlinedIcon sx={{ fontSize: 14 }} />}>
            Group by
          </FilterSectionLabel>
          <TextField
            select size="small" fullWidth
            value={dimension}
            onChange={(e) => onDimension(e.target.value as any)}
          >
            <MenuItem value="building">Building</MenuItem>
            <MenuItem value="floor">Floor</MenuItem>
            <MenuItem value="area">Area</MenuItem>
            <MenuItem value="location">Location</MenuItem>
            <MenuItem value="location_type">Location type</MenuItem>
            <MenuItem value="site">Site</MenuItem>
            <MenuItem value="tags">Tags</MenuItem>
          </TextField>
          {/* Tag picker — only shown when the Tags dimension is
              selected. Multi-select via MUI's own select. Icons match
              each tag's own colour so admins can eyeball the selection. */}
          {dimension === 'tags' && (
            <Box sx={{ mt: 1.25 }}>
              <TextField
                select size="small" fullWidth
                SelectProps={{
                  multiple: true,
                  displayEmpty: true,
                  renderValue: (v) => {
                    const ids = v as string[]
                    if (ids.length === 0) return (
                      <Box component="span" sx={{ color: 'text.disabled', fontSize: 13 }}>
                        All tags — pick to narrow
                      </Box>
                    )
                    return (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {selectedTags.map((t) => (
                          <Chip key={t.id} size="small" label={t.name}
                            sx={{
                              height: 20, fontSize: 10.5, fontWeight: 700,
                              bgcolor: `${t.color}22`, color: t.color,
                              border: '1px solid', borderColor: `${t.color}55`,
                            }} />
                        ))}
                      </Box>
                    )
                  },
                }}
                value={tagIds}
                onChange={(e) => {
                  const raw = e.target.value
                  onTagIds(typeof raw === 'string' ? raw.split(',') : raw as string[])
                }}
              >
                {tagsQ.isLoading && (
                  <MenuItem disabled>
                    <Box component="span" sx={{ color: 'text.disabled' }}>Loading tags…</Box>
                  </MenuItem>
                )}
                {!tagsQ.isLoading && allTags.length === 0 && (
                  <MenuItem disabled>
                    <Box component="span" sx={{ color: 'text.disabled' }}>
                      No tags yet — assign one from any site or location detail.
                    </Box>
                  </MenuItem>
                )}
                {allTags.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width: 10, height: 10, borderRadius: 99, bgcolor: t.color,
                        boxShadow: `0 0 0 3px ${t.color}22`, flexShrink: 0 }} />
                      <span>{t.name}</span>
                      <Typography sx={{ fontSize: 11, color: 'text.disabled', ml: 0.5 }}>
                        · {t.assigned_site_count} site{t.assigned_site_count === 1 ? '' : 's'}
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}
        </Grid>
      </Grid>

      {/* ── Type — filters/scopes the entire dashboard by kind ── */}
      <Box sx={{
        px: { xs: 2, md: 2.5 }, pb: { xs: 2, md: 2.5 }, pt: 0.5,
        borderTop: '1px solid', borderColor: 'divider', mt: 0.5,
      }}>
        <FilterSectionLabel icon={<ChecklistRtlOutlinedIcon sx={{ fontSize: 14 }} />}>
          Type
        </FilterSectionLabel>
        <ToggleButtonGroup
          size="small" value={itemType} exclusive
          onChange={(_, v) => v && onItemType(v)}
        >
          {(!allowedItemTypes || allowedItemTypes.includes('all')) && <ToggleButton value="all">All</ToggleButton>}
          {(!allowedItemTypes || allowedItemTypes.includes('work_order')) && <ToggleButton value="work_order">Work orders</ToggleButton>}
          {(!allowedItemTypes || allowedItemTypes.includes('task')) && <ToggleButton value="task">Tasks</ToggleButton>}
          {(!allowedItemTypes || allowedItemTypes.includes('checklist')) && <ToggleButton value="checklist">Checklists</ToggleButton>}
        </ToggleButtonGroup>
      </Box>

      {/* ── Task Type — scopes the entire dashboard by work_type ── */}
      <Box sx={{
        px: { xs: 2, md: 2.5 }, pb: { xs: 2, md: 2.5 }, pt: 0.5,
        borderTop: '1px solid', borderColor: 'divider', mt: 0.5,
      }}>
        <FilterSectionLabel icon={<AssignmentTurnedInOutlinedIcon sx={{ fontSize: 14 }} />}>
          Task Type
        </FilterSectionLabel>
        <ToggleButtonGroup
          size="small" value={workType} exclusive
          onChange={(_, v) => v && onWorkType(v)}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="preventive">Preventive</ToggleButton>
          <ToggleButton value="inspection">Inspection</ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </Box>
  )
}

// Shared label style for each filter section so the eye groups
// related controls without any horizontal dividers cluttering the
// surface.
function FilterSectionLabel({
  icon, children,
}: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
      <Box sx={{ color: 'text.disabled', display: 'flex' }}>{icon}</Box>
      <Typography sx={{
        fontSize: 10.5, fontWeight: 800, letterSpacing: 0.8,
        textTransform: 'uppercase', color: 'text.disabled',
      }}>
        {children}
      </Typography>
    </Stack>
  )
}

// Consistent visual sizing for the native date inputs — without
// these the chrome of `type=date` looks tiny next to MUI's Select.
const dateInputSx = {
  flex: 1,
  '& .MuiOutlinedInput-root': {
    fontFamily: 'ui-monospace, monospace',
    fontSize: 13,
  },
  '& input': { py: 1.05 },
} as const

// ─── KPI strip ──────────────────────────────────────────────────────────────

function KpiStrip({ data, woLabel, checklistOnly, hideChecklist }: {
  data: DetailedDashboardData
  woLabel: string
  checklistOnly: boolean
  hideChecklist: boolean
}) {
  const wo = data.kpis.workOrders
  const cl = data.kpis.checklists
  const byKind = data.kpis.byKind
  // Headline numbers mirror the sample report's 4-card layout:
  // Total Items / Completed / Missed / Compliance Rate. Normally each
  // card shows the combined WO + checklist figure as the hero, with
  // the sub-line breaking down the split. In "Checklists" display
  // mode the hero becomes checklist-only, and in "Work orders"/"Tasks"
  // mode it becomes WO-only — either way, showing a combined total
  // that pulls in the OTHER kind would contradict a filter whose whole
  // point is "just this one".
  const totalScheduled = checklistOnly ? cl.scheduled : hideChecklist ? wo.scheduled : wo.scheduled + cl.scheduled
  const totalCompleted = checklistOnly ? cl.submitted : hideChecklist ? wo.completed : wo.completed + cl.submitted
  const totalMissed    = checklistOnly
    ? data.missed.filter((m) => m.kind === 'checklist').length
    : hideChecklist
      ? data.missed.filter((m) => m.kind !== 'checklist').length
      : data.missed.length
  const combinedPct = checklistOnly ? cl.compliance
    : hideChecklist ? wo.compliance
    : totalScheduled > 0 ? Math.round((totalCompleted / totalScheduled) * 1000) / 10 : 0
  const tiles: Array<{
    label: string; value: string; sub: string; color: string; icon: React.ReactNode
  }> = [
    {
      label: 'Total items',
      value: totalScheduled.toLocaleString(),
      sub: checklistOnly
        ? `${cl.scheduled.toLocaleString()} checklist rounds · ${data.range.days} days`
        : hideChecklist
          ? `${wo.scheduled.toLocaleString()} ${woLabel.toLowerCase()} · ${data.range.days} days`
          : `${byKind.work_order.scheduled.toLocaleString()} work orders · ${byKind.task.scheduled.toLocaleString()} tasks · ${cl.scheduled.toLocaleString()} checklist · ${data.range.days} days`,
      color: '#6366F1',
      icon: <PercentOutlinedIcon sx={{ fontSize: 18 }} />,
    },
    {
      label: 'Completed',
      value: totalCompleted.toLocaleString(),
      sub: checklistOnly
        ? `${cl.submitted.toLocaleString()} checklists submitted`
        : hideChecklist
          ? `${wo.completed.toLocaleString()} ${woLabel.toLowerCase()} closed`
          : `${byKind.work_order.completed.toLocaleString()} work orders closed · ${byKind.task.completed.toLocaleString()} tasks closed · ${cl.submitted.toLocaleString()} checklists submitted`,
      color: '#10B981',
      icon: <AssignmentTurnedInOutlinedIcon sx={{ fontSize: 18 }} />,
    },
    {
      label: 'Missed',
      value: totalMissed.toLocaleString(),
      sub: 'past-due, never completed',
      color: totalMissed > 0 ? '#EF4444' : '#10B981',
      icon: <EventBusyOutlinedIcon sx={{ fontSize: 18 }} />,
    },
    ...(checklistOnly ? [] : [{
      label: hideChecklist ? `Ongoing ${woLabel.toLowerCase()}` : 'Ongoing items',
      value: data.kpis.ongoing.toLocaleString(),
      sub: 'currently open or in progress',
      color: '#0EA5E9',
      icon: <AssignmentTurnedInOutlinedIcon sx={{ fontSize: 18 }} />,
    }]),
    {
      label: 'Compliance rate',
      value: `${combinedPct.toFixed(1)}%`,
      sub: checklistOnly
        ? `Checklist ${cl.compliance.toFixed(1)}%`
        : hideChecklist
          ? `${woLabel} ${wo.compliance.toFixed(1)}%`
          : `Work orders ${pct(byKind.work_order.completed, byKind.work_order.scheduled).toFixed(1)}% · Tasks ${pct(byKind.task.completed, byKind.task.scheduled).toFixed(1)}% · Checklist ${cl.compliance.toFixed(1)}%`,
      color: complianceColor(combinedPct),
      icon: <ChecklistRtlOutlinedIcon sx={{ fontSize: 18 }} />,
    },
  ]
  const mdSize = tiles.length === 5 ? 2.4 : 3
  return (
    <Grid container spacing={2}>
      {tiles.map((t, i) => (
        <Grid key={i} size={{ xs: 12, sm: 6, md: mdSize }}>
          <Box sx={{
            p: 2, borderRadius: 2,
            bgcolor: 'background.paper',
            border: '1px solid', borderColor: 'divider',
            position: 'relative', overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute', top: 0, left: 0, right: 0, height: 3,
              bgcolor: t.color,
            },
          }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6,
                textTransform: 'uppercase', color: 'text.secondary' }}>
                {t.label}
              </Typography>
              <Box sx={{
                width: 28, height: 28, borderRadius: 1,
                bgcolor: alpha(t.color, 0.12), color: t.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>{t.icon}</Box>
            </Stack>
            <Typography sx={{ fontSize: 26, fontWeight: 800, color: t.color,
              fontFamily: 'ui-monospace, monospace', letterSpacing: '-0.02em',
              lineHeight: 1 }}>
              {t.value}
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.75 }}>
              {t.sub}
            </Typography>
          </Box>
        </Grid>
      ))}
    </Grid>
  )
}

// ─── Daily compliance bar chart ─────────────────────────────────────────────
//
// Pure SVG bars rather than @mui/x-charts so we get the per-bar colour
// coding (green/amber/red by threshold) and the side-by-side WO+checklist
// pair without fighting the chart library's series defaults.

function DailyTrendPanel({ data, woLabelShort, checklistOnly, hideChecklist }: {
  data: DetailedDashboardData
  woLabelShort: string
  checklistOnly: boolean
  /** True when the top-level Type filter is "Work orders" or "Tasks" —
   *  checklists don't apply to this view at all, so the series toggle
   *  and any checklist bars are dropped entirely rather than just
   *  relabelled. */
  hideChecklist: boolean
}) {
  const [series, setSeries] = useState<'both' | 'wo' | 'checklist'>('both')
  // "Checklists" display mode forces the series regardless of local
  // toggle state — there's no WO data to show alongside it. "Work
  // orders"/"Tasks" mode forces the opposite — no checklist series
  // applies, and the bar switches to a work-type breakdown instead of
  // a single WO/Task series (see below). Either way the toggle itself
  // is hidden rather than left dangling on an option that can't do
  // anything.
  const effSeries = checklistOnly ? 'checklist' : hideChecklist ? 'wo' : series
  const showToggle = !checklistOnly && !hideChecklist
  const maxBars = 60
  // Cap to last `maxBars` days so the chart never gets stuck rendering
  // a 365-day window with sub-pixel bars. The slice is from the tail so
  // the most recent days are always visible.
  const rows = data.daily.length > maxBars
    ? data.daily.slice(-maxBars)
    : data.daily

  // Every work_type seen across the visible window, in a stable order
  // (alphabetical) so a given type keeps the same stacking position
  // and colour from day to day rather than jittering as the daily mix
  // changes. Only relevant in "Work orders"/"Tasks" mode.
  const workTypes = useMemo(() => {
    if (!hideChecklist) return []
    const seen = new Set<string>()
    for (const r of rows) for (const wt of Object.keys(r.wo.byWorkType)) seen.add(wt)
    return [...seen].sort()
  }, [rows, hideChecklist])

  // Bar height encodes the actual completed/submitted COUNT for the
  // day (scaled against the tallest bar in the visible window) — not
  // a percentage, so a day with 40 completed items reads visibly
  // taller than a day with 4, which a %-based bar would flatten to
  // "both near 100%". In work-type mode the "bar" is really a stack of
  // segments, so the scale is against each day's STACK TOTAL.
  const showWoDefault = effSeries === 'both' || effSeries === 'wo'
  const showClDefault = effSeries === 'both' || effSeries === 'checklist'
  const maxVal = hideChecklist
    ? Math.max(1, ...rows.map((r) => Object.values(r.wo.byWorkType).reduce((s, v) => s + v, 0)))
    : Math.max(1, ...rows.flatMap((r) => [
        ...(showWoDefault ? [r.wo.completed] : []),
        ...(showClDefault ? [r.checklist.submitted] : []),
      ]))

  return (
    <Box sx={{
      p: 2.5, borderRadius: 2,
      bgcolor: 'background.paper',
      border: '1px solid', borderColor: 'divider',
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 800, textTransform: 'uppercase',
            letterSpacing: '0.08em', color: 'text.secondary' }}>
            Daily completion rate
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.25 }}>
            {hideChecklist
              ? `Bar height = completed count, stacked by work type`
              : `Bar height = completed${hideChecklist ? '' : ' / submitted'} count`}
          </Typography>
        </Box>
        {showToggle && (
          <ToggleButtonGroup
            size="small" value={series} exclusive
            onChange={(_, v) => v && setSeries(v)}
          >
            <ToggleButton value="both">Both</ToggleButton>
            <ToggleButton value="wo">{woLabelShort}</ToggleButton>
            <ToggleButton value="checklist">Checklist</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Stack>

      <Box sx={{ position: 'relative', height: 220, display: 'flex',
        alignItems: 'flex-end', gap: 0.5, px: 1 }}>
        {/* Grid lines — plain proportional scale markers, not tied to
            any particular threshold now that height = count. */}
        {[25, 50, 75].map((y) => (
          <Box key={y} sx={{
            position: 'absolute', left: 0, right: 0,
            bottom: `${y}%`,
            borderTop: '1px dashed', borderColor: 'divider', opacity: 0.5,
          }} />
        ))}
        {rows.length === 0 && (
          <Box sx={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'text.disabled', fontSize: 12.5,
          }}>
            No scheduled work in this window yet — try a wider date range or
            change the site / location filters.
          </Box>
        )}
        {rows.map((row, i) => {
          if (hideChecklist) {
            const total = Object.values(row.wo.byWorkType).reduce((s, v) => s + v, 0)
            return (
              <Tooltip key={i} arrow placement="top"
                title={
                  <Box sx={{ fontSize: 11 }}>
                    <div><b>{shortDate(row.date)}</b></div>
                    {total === 0
                      ? <div>No completed {woLabelShort.toLowerCase()}s</div>
                      : workTypes
                        .filter((wt) => row.wo.byWorkType[wt])
                        .map((wt) => (
                          <div key={wt}>{workTypeLabel(wt)}: {row.wo.byWorkType[wt]}</div>
                        ))}
                  </Box>
                }
              >
                <Stack justifyContent="flex-end"
                  sx={{ flex: 1, height: '100%', minWidth: 0, cursor: 'default' }}>
                  {/* Stack renders bottom-up in visual order, so reverse
                      the (alphabetical) type list — the last DOM child
                      of a column-reverse-free Stack sits on top. */}
                  {[...workTypes].reverse().map((wt) => {
                    const v = row.wo.byWorkType[wt] ?? 0
                    if (v === 0) return null
                    return (
                      <Box key={wt} sx={{
                        width: '70%', mx: 'auto',
                        height: `${Math.max((v / maxVal) * 100, 2)}%`,
                        bgcolor: workTypeColor(wt),
                        opacity: 0.95,
                        // Stack sits flex-end in its column, so the
                        // FIRST rendered segment is the bottom-most one
                        // (touches the axis) and the LAST is the
                        // top-most — round each one's outward edge.
                        '&:first-of-type': { borderRadius: '0 0 1px 1px' },
                        '&:last-of-type': { borderRadius: '3px 3px 0 0' },
                        transition: 'height 300ms',
                        '&:hover': { opacity: 1 },
                      }} />
                    )
                  })}
                </Stack>
              </Tooltip>
            )
          }
          const showWo = effSeries === 'both' || effSeries === 'wo'
          const showCl = effSeries === 'both' || effSeries === 'checklist'
          const wEach = showWo && showCl ? '40%' : '70%'
          return (
            <Stack key={i} direction="row" justifyContent="center" spacing="2px"
              sx={{
                flex: 1, height: '100%', alignItems: 'flex-end',
                minWidth: 0,
              }}>
              {showWo && (
                <Tooltip arrow placement="top"
                  title={
                    <Box sx={{ fontSize: 11 }}>
                      <div><b>{shortDate(row.date)}</b></div>
                      <div>{woLabelShort}: {row.wo.completed}/{row.wo.scheduled} completed</div>
                    </Box>
                  }
                >
                  <Box sx={{
                    width: wEach, height: `${Math.max((row.wo.completed / maxVal) * 100, row.wo.completed > 0 ? 2 : 0)}%`,
                    bgcolor: '#0D9488',
                    borderRadius: '3px 3px 1px 1px',
                    opacity: row.wo.scheduled === 0 ? 0.25 : 0.95,
                    transition: 'opacity 150ms, height 300ms',
                    '&:hover': { opacity: 1 },
                  }} />
                </Tooltip>
              )}
              {showCl && (
                <Tooltip arrow placement="top"
                  title={
                    <Box sx={{ fontSize: 11 }}>
                      <div><b>{shortDate(row.date)}</b></div>
                      <div>Checklist: {row.checklist.submitted}/{row.checklist.scheduled} submitted</div>
                    </Box>
                  }
                >
                  <Box sx={{
                    width: wEach, height: `${Math.max((row.checklist.submitted / maxVal) * 100, row.checklist.submitted > 0 ? 2 : 0)}%`,
                    bgcolor: '#7C3AED',
                    borderRadius: '3px 3px 1px 1px',
                    opacity: row.checklist.scheduled === 0 ? 0.25 : 0.95,
                    border: '1px dashed', borderColor: 'background.paper',
                    transition: 'opacity 150ms, height 300ms',
                    '&:hover': { opacity: 1 },
                  }} />
                </Tooltip>
              )}
            </Stack>
          )
        })}
      </Box>

      {/* X-axis labels — only every Nth one so they don't overlap */}
      <Stack direction="row" sx={{ mt: 0.5, px: 1 }}>
        {rows.map((row, i) => {
          const stride = Math.max(1, Math.ceil(rows.length / 12))
          const show = i % stride === 0
          return (
            <Box key={i} sx={{
              flex: 1, textAlign: 'center',
              fontSize: 9.5, color: 'text.disabled',
              fontFamily: 'ui-monospace, monospace',
              whiteSpace: 'nowrap', overflow: 'hidden',
            }}>
              {show ? shortDate(row.date) : ''}
            </Box>
          )
        })}
      </Stack>

      {/* Legend */}
      <Stack direction="row" spacing={2} sx={{ mt: 1.5, pt: 1, flexWrap: 'wrap', rowGap: 1,
        borderTop: '1px solid', borderColor: 'divider' }}>
        {hideChecklist ? (
          workTypes.length === 0
            ? <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>No completed items in this window yet.</Typography>
            : workTypes.map((wt) => (
              <Stack key={wt} direction="row" alignItems="center" spacing={0.75}>
                <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: workTypeColor(wt) }} />
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{workTypeLabel(wt)}</Typography>
              </Stack>
            ))
        ) : (
          <>
            {effSeries !== 'checklist' && (
              <LegendDot label={`${woLabelShort === 'Task' ? 'Tasks' : 'Work orders'} (teal)`} sample="solid" color="#0D9488" />
            )}
            {effSeries !== 'wo' && (
              <LegendDot label="Checklists (purple)" sample="dashed" color="#7C3AED" />
            )}
          </>
        )}
      </Stack>
    </Box>
  )
}

function LegendDot({ label, sample, color = '#10B981' }: {
  label: string
  sample: 'solid' | 'dashed'
  color?: string
}) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <Box sx={{
        width: 14, height: 14, borderRadius: 0.75,
        bgcolor: color,
        border: sample === 'dashed' ? '1.5px dashed' : 'none',
        borderColor: 'background.paper',
        boxShadow: sample === 'dashed' ? `inset 0 0 0 1px ${color}` : 'none',
      }} />
      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{label}</Typography>
    </Stack>
  )
}

// ─── Heatmap ────────────────────────────────────────────────────────────────

function HeatmapPanel({ data, checklistOnly, hideChecklist, woLabelShort }: {
  data: DetailedDashboardData
  checklistOnly: boolean
  hideChecklist: boolean
  woLabelShort: string
}) {
  const [series, setSeries] = useState<'combined' | 'wo' | 'checklist'>('combined')
  const effSeries = checklistOnly ? 'checklist' : hideChecklist ? 'wo' : series
  const cells = useMemo(() => data.daily.map((row) => {
    const pct = effSeries === 'wo' ? row.wo.compliance
      : effSeries === 'checklist' ? row.checklist.compliance
      : combinedCompliance(row)
    const empty = (effSeries === 'wo' && row.wo.scheduled === 0)
      || (effSeries === 'checklist' && row.checklist.scheduled === 0)
      || (effSeries === 'combined' && (row.wo.scheduled + row.checklist.scheduled) === 0)
    return { date: row.date, pct, empty, row }
  }), [data.daily, effSeries])

  return (
    <Box sx={{
      p: 2.5, borderRadius: 2,
      bgcolor: 'background.paper',
      border: '1px solid', borderColor: 'divider',
      height: '100%',
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 800, textTransform: 'uppercase',
          letterSpacing: '0.08em', color: 'text.secondary' }}>
          Completion heatmap
        </Typography>
        {!checklistOnly && !hideChecklist && (
          <ToggleButtonGroup
            size="small" value={series} exclusive
            onChange={(_, v) => v && setSeries(v)}
          >
            <ToggleButton value="combined">All</ToggleButton>
            <ToggleButton value="wo">WO</ToggleButton>
            <ToggleButton value="checklist">Chk</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Stack>

      {cells.length === 0 && (
        <Box sx={{
          py: 4, textAlign: 'center',
          color: 'text.disabled', fontSize: 12.5,
        }}>
          No daily data yet for this window.
        </Box>
      )}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(28px, 1fr))',
        gap: '3px',
      }}>
        {cells.map((c, i) => (
          <Tooltip key={i} arrow placement="top"
            title={
              <Box sx={{ fontSize: 11 }}>
                <div><b>{shortDate(c.date)}</b></div>
                {c.empty ? <div>No scheduled work</div> : (
                  <>
                    <div>{woLabelShort}: {c.row.wo.compliance.toFixed(1)}% ({c.row.wo.completed}/{c.row.wo.scheduled})</div>
                    {!hideChecklist && (
                      <div>Checklist: {c.row.checklist.compliance.toFixed(1)}% ({c.row.checklist.submitted}/{c.row.checklist.scheduled})</div>
                    )}
                  </>
                )}
              </Box>
            }
          >
            <Box sx={{
              aspectRatio: '1',
              borderRadius: 0.75,
              bgcolor: c.empty ? 'action.hover'
                : alpha(complianceColor(c.pct), 0.2 + Math.min(0.8, c.pct / 100 * 0.7)),
              border: '1px solid',
              borderColor: c.empty ? 'divider' : alpha(complianceColor(c.pct), 0.4),
              cursor: 'pointer',
              transition: 'transform 120ms',
              '&:hover': { transform: 'scale(1.15)', zIndex: 1 },
            }} />
          </Tooltip>
        ))}
      </Box>

      <Stack direction="row" spacing={1.5} sx={{ mt: 2, pt: 1,
        borderTop: '1px solid', borderColor: 'divider', fontSize: 11 }}>
        {[
          { c: '#10B981', l: '≥80%' },
          { c: '#F59E0B', l: '65–79%' },
          { c: '#EF4444', l: '<65%' },
        ].map((s) => (
          <Stack key={s.l} direction="row" alignItems="center" spacing={0.5}>
            <Box sx={{ width: 10, height: 10, borderRadius: 0.5,
              bgcolor: alpha(s.c, 0.6) }} />
            <Typography sx={{ fontSize: 10.5, color: 'text.secondary' }}>{s.l}</Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  )
}

function combinedCompliance(row: DetailedDashboardData['daily'][number]): number {
  const num = row.wo.completed + row.checklist.submitted
  const den = row.wo.scheduled + row.checklist.scheduled
  return den > 0 ? Math.round((num / den) * 1000) / 10 : 0
}

// ─── Location breakdown ─────────────────────────────────────────────────────

function BreakdownPanel({
  data, dimension, woLabelShort, checklistOnly, hideChecklist,
}: {
  data: DetailedDashboardData
  dimension: DetailedDashboardData['filters']['dimension']
  woLabelShort: string
  checklistOnly: boolean
  hideChecklist: boolean
}) {
  const [series, setSeries] = useState<'wo' | 'checklist'>('wo')
  const effSeries = checklistOnly ? 'checklist' : hideChecklist ? 'wo' : series
  const rows = data.breakdown
  const dimLabel = ({
    building: 'Building', floor: 'Floor', area: 'Area',
    location: 'Location', location_type: 'Location type', site: 'Site',
    // Tag dimension doesn't have a distinct breakdown grouping yet —
    // the backend still returns the default site rollup. Label reads
    // as "Site" so the panel copy isn't misleading.
    tags: 'Site',
  } as const)[dimension]

  // Sort by scheduled volume so the biggest workloads are at the top.
  const sorted = useMemo(() => [...rows].sort((a, b) => {
    const aSch = effSeries === 'wo' ? a.wo.scheduled : a.checklist.scheduled
    const bSch = effSeries === 'wo' ? b.wo.scheduled : b.checklist.scheduled
    return bSch - aSch
  }), [rows, effSeries])

  return (
    <Box sx={{
      p: 2.5, borderRadius: 2,
      bgcolor: 'background.paper',
      border: '1px solid', borderColor: 'divider',
      height: '100%',
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 800, textTransform: 'uppercase',
            letterSpacing: '0.08em', color: 'text.secondary' }}>
            {dimLabel}-wise performance
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.25 }}>
            Change the "Group by" filter at the top to switch dimensions.
          </Typography>
        </Box>
        {!checklistOnly && !hideChecklist && (
          <ToggleButtonGroup
            size="small" value={series} exclusive
            onChange={(_, v) => v && setSeries(v)}
          >
            <ToggleButton value="wo">{woLabelShort}</ToggleButton>
            <ToggleButton value="checklist">Checklist</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Stack>

      {sorted.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.disabled' }}>
          <Typography sx={{ fontSize: 13 }}>No data for this dimension in the selected range.</Typography>
        </Box>
      ) : (
        <Stack spacing={1.25} sx={{ maxHeight: 360, overflowY: 'auto' }}>
          {sorted.map((row) => {
            const bucket = effSeries === 'wo' ? row.wo : row.checklist
            const pct = bucket.compliance
            const color = complianceColor(pct)
            const completedKey = effSeries === 'wo' ? 'completed' : 'submitted'
            return (
              <Box key={row.id}>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline"
                  sx={{ mb: 0.4 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.primary',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%' }}
                    title={row.name}>
                    {row.name}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color, fontWeight: 700,
                    fontFamily: 'ui-monospace, monospace' }}>
                    {pct.toFixed(1)}%
                    <Box component="span" sx={{ color: 'text.disabled', fontWeight: 400, ml: 0.75 }}>
                      ({(bucket as any)[completedKey]}/{bucket.scheduled})
                    </Box>
                  </Typography>
                </Stack>
                <Box sx={{ height: 6, borderRadius: 99, bgcolor: 'action.hover',
                  overflow: 'hidden' }}>
                  <Box sx={{
                    height: '100%',
                    width: `${Math.min(100, pct)}%`,
                    bgcolor: color,
                    borderRadius: 99,
                    transition: 'width 600ms cubic-bezier(0.32, 0.72, 0, 1)',
                  }} />
                </Box>
              </Box>
            )
          })}
        </Stack>
      )}
    </Box>
  )
}

// ─── Missed items table ─────────────────────────────────────────────────────

function MissedPanel({ data, itemType }: {
  data: DetailedDashboardData
  itemType: 'all' | 'work_order' | 'task' | 'checklist'
}) {
  const [kind, setKind] = useState<'all' | 'work_order' | 'task' | 'checklist'>('all')
  // Which chips make sense depends on the main Type filter: scoped to
  // "Work orders" or "Tasks", the missed list can never contain rows
  // of the OTHER kind (the server already restricted it), so offering
  // that chip would just be a filter that always empties the list.
  const validKinds = (itemType === 'work_order' ? ['all', 'work_order', 'checklist']
    : itemType === 'task' ? ['all', 'task', 'checklist']
    : ['all', 'work_order', 'task', 'checklist']) as Array<'all' | 'work_order' | 'task' | 'checklist'>
  // "Checklists" display mode forces the local chip filter regardless
  // of its own state — the server didn't restrict `data.missed` for
  // this mode, so filtering client-side is the only way to actually
  // show checklist-only rows. Falling back to 'all' guards the case
  // where the Type filter changed out from under a previously-picked
  // chip that's no longer offered (e.g. "Task" was selected, then the
  // Type filter flipped to "Work orders").
  const effKind = itemType === 'checklist' ? 'checklist'
    : validKinds.includes(kind) ? kind : 'all'
  const rows = useMemo(() => {
    if (effKind === 'all') return data.missed
    return data.missed.filter((r) => r.kind === effKind)
  }, [data.missed, effKind])
  const kindLabel = (k: 'all' | 'work_order' | 'task' | 'checklist') =>
    k === 'all' ? 'All' : k === 'work_order' ? 'Work orders' : k === 'task' ? 'Tasks' : 'Checklists'

  return (
    <Box sx={{
      borderRadius: 2,
      bgcolor: 'background.paper',
      border: '1px solid', borderColor: 'divider',
      overflow: 'hidden',
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ p: 2.5, pb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 800, textTransform: 'uppercase',
            letterSpacing: '0.08em', color: 'text.secondary' }}>
            Missed locations
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.25 }}>
            Scheduled in the window, still incomplete. Top {rows.length} by days overdue.
          </Typography>
        </Box>
        {itemType !== 'checklist' && (
        <Stack direction="row" spacing={1}>
          {validKinds.map((k) => (
            <Chip key={k}
              label={kindLabel(k)}
              size="small"
              color={effKind === k ? 'primary' : 'default'}
              variant={effKind === k ? 'filled' : 'outlined'}
              onClick={() => setKind(k)}
              sx={{ fontSize: 11, fontWeight: 600, cursor: 'pointer' }} />
          ))}
        </Stack>
        )}
      </Stack>

      {rows.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 5, color: 'text.disabled' }}>
          <Typography sx={{ fontSize: 13 }}>
            Nothing missed — everything in the window has been closed or submitted.
          </Typography>
        </Box>
      ) : (
        <Box component="table" sx={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 12.5,
        }}>
          <Box component="thead">
            <Box component="tr" sx={{
              '& th': {
                textAlign: 'left',
                px: 2, py: 1,
                fontSize: 10.5, fontWeight: 700, letterSpacing: 0.5,
                textTransform: 'uppercase', color: 'text.disabled',
                borderTop: '1px solid', borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFA' : 'rgba(255,255,255,0.02)',
              },
            }}>
              <Box component="th">#</Box>
              <Box component="th">Kind</Box>
              <Box component="th">Title</Box>
              <Box component="th">Code</Box>
              <Box component="th">Site / Location</Box>
              <Box component="th" sx={{ textAlign: 'right !important' }}>Days overdue</Box>
            </Box>
          </Box>
          <Box component="tbody">
            {rows.map((r, i) => {
              const sevColor = r.days_overdue >= 14 ? '#EF4444'
                : r.days_overdue >= 7 ? '#F59E0B' : '#64748B'
              return (
                <Box component="tr" key={`${r.kind}-${r.id}`}
                  sx={{
                    '& td': {
                      px: 2, py: 1.1,
                      borderBottom: '1px solid', borderColor: 'divider',
                    },
                    '&:hover td': {
                      bgcolor: (t) => t.palette.mode === 'light'
                        ? 'rgba(0,0,0,0.015)'
                        : 'rgba(255,255,255,0.02)',
                    },
                  }}>
                  <Box component="td" sx={{ color: 'text.disabled',
                    fontFamily: 'ui-monospace, monospace', fontSize: 11, width: 36 }}>
                    {String(i + 1).padStart(2, '0')}
                  </Box>
                  <Box component="td">
                    <Chip size="small"
                      label={r.kind === 'work_order' ? 'WO' : r.kind === 'task' ? 'Task' : 'Checklist'}
                      sx={{
                        height: 20, fontSize: 10, fontWeight: 700,
                        bgcolor: r.kind === 'checklist'
                          ? alpha('#7C3AED', 0.12) : alpha('#0D9488', 0.12),
                        color: r.kind === 'checklist' ? '#7C3AED' : '#0D9488',
                      }} />
                  </Box>
                  <Box component="td" sx={{ fontWeight: 600 }}>{r.title}</Box>
                  <Box component="td" sx={{
                    fontFamily: 'ui-monospace, monospace',
                    fontSize: 11, color: 'text.secondary' }}>
                    {r.code ?? '—'}
                  </Box>
                  <Box component="td" sx={{ color: 'text.secondary' }}>
                    {r.site_name || '—'}
                    {r.location_name && (
                      <Box component="span" sx={{ color: 'text.disabled' }}>
                        {' · '}{r.location_name}
                      </Box>
                    )}
                  </Box>
                  <Box component="td" sx={{
                    textAlign: 'right',
                    fontFamily: 'ui-monospace, monospace',
                    fontWeight: 700, color: sevColor }}>
                    {r.days_overdue}d
                  </Box>
                </Box>
              )
            })}
          </Box>
        </Box>
      )}
    </Box>
  )
}

// ─── People leaderboard ─────────────────────────────────────────────────────
//
// Compliance per active user — driven by the same date / location
// filters as the rest of the dashboard, with its own role-scoping
// dropdown that re-fetches the parent (server narrows the people
// array to one role). Shows initials, role pill, completed-vs-
// scheduled bar, and a combined activity counter.

function PeoplePanel({ data, roleId, onRoleId, woLabelShort, checklistOnly, hideChecklist }: {
  data: DetailedDashboardData
  roleId: string
  onRoleId: (v: string) => void
  woLabelShort: string
  checklistOnly: boolean
  hideChecklist: boolean
}) {
  // Both single-kind modes re-rank by their own figure alone — the
  // server's `activity` (and its ordering) is always the combined WO +
  // checklist count, which would misrank people under either filter
  // (e.g. someone who never touches a checklist would rank below
  // someone who does, even in "Work orders" mode where that shouldn't
  // matter at all).
  const people = useMemo(() => {
    if (checklistOnly) return [...data.people].sort((a, b) => b.checklist.submitted - a.checklist.submitted)
    if (hideChecklist)  return [...data.people].sort((a, b) => b.wo.completed - a.wo.completed)
    return data.people
  }, [data.people, checklistOnly, hideChecklist])
  const initialsOf = (name: string) =>
    name.split(/\s+/).slice(0, 2).map((p) => p[0] ?? '').join('').toUpperCase() || '?'
  const roleColor = (name: string) => {
    // Soft, distinct chip colours per role label. Hash-based so the
    // same role keeps the same colour across re-renders.
    const palette = ['#0D9488', '#7C3AED', '#2563EB', '#DB2777', '#F59E0B', '#0EA5E9']
    let h = 0
    for (let i = 0; i < name.length; i++) h = ((h << 5) - h + name.charCodeAt(i)) | 0
    return palette[Math.abs(h) % palette.length]!
  }

  return (
    <Box sx={{
      p: 2.5, borderRadius: 2,
      bgcolor: 'background.paper',
      border: '1px solid', borderColor: 'divider',
      height: '100%', display: 'flex', flexDirection: 'column',
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 800, textTransform: 'uppercase',
            letterSpacing: '0.08em', color: 'text.secondary' }}>
            User's overview
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.25 }}>
            {checklistOnly
              ? 'Ranked by submitted checklists in this window.'
              : hideChecklist
                ? `Ranked by completed ${woLabelShort.toLowerCase()}s in this window.`
                : `Ranked by completed ${woLabelShort.toLowerCase()}s + submitted checklists in this window.`}
          </Typography>
        </Box>
        <TextField
          select size="small"
          value={roleId}
          onChange={(e) => onRoleId(e.target.value)}
          sx={{ minWidth: 160 }}
          SelectProps={{ displayEmpty: true }}
        >
          <MenuItem value="">All roles</MenuItem>
          {data.roles.map((r) => (
            <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
          ))}
        </TextField>
      </Stack>

      {people.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.disabled', flex: 1 }}>
          <Typography sx={{ fontSize: 13 }}>
            No activity in this window {roleId ? 'for the selected role' : ''}.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.25} sx={{ overflowY: 'auto', maxHeight: 400, pr: 0.5 }}>
          {people.map((p, i) => {
            const rc = roleColor(p.role_name || 'Member')
            return (
              <Box key={p.id} sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                p: 1.25, borderRadius: 1.5,
                bgcolor: 'action.hover',
                transition: 'background-color 120ms',
                '&:hover': { bgcolor: 'action.selected' },
              }}>
                {/* Rank */}
                <Box sx={{
                  width: 24, textAlign: 'center',
                  fontSize: 11, fontWeight: 800,
                  fontFamily: 'ui-monospace, monospace',
                  color: i < 3 ? 'primary.main' : 'text.disabled',
                }}>
                  {String(i + 1).padStart(2, '0')}
                </Box>

                {/* Avatar */}
                {p.avatar_url ? (
                  <Box component="img" src={p.avatar_url} alt=""
                    sx={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover',
                      flexShrink: 0 }} />
                ) : (
                  <Box sx={{
                    width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                    bgcolor: alpha(rc, 0.18), color: rc,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, fontWeight: 800,
                  }}>
                    {initialsOf(p.name)}
                  </Box>
                )}

                {/* Name + role + activity bar */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.4 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.name}
                    </Typography>
                    {p.role_name && (
                      <Chip size="small" label={p.role_name}
                        sx={{
                          height: 18, fontSize: 9.5, fontWeight: 700,
                          bgcolor: alpha(rc, 0.12), color: rc,
                          textTransform: 'capitalize',
                        }} />
                    )}
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1.25}
                    sx={{ fontSize: 10.5, color: 'text.secondary' }}>
                    {!checklistOnly && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#0D9488' }} />
                        {p.wo.completed}/{p.wo.scheduled} {woLabelShort}
                      </Box>
                    )}
                    {!hideChecklist && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#7C3AED' }} />
                        {p.checklist.submitted} chk
                      </Box>
                    )}
                  </Stack>
                </Box>

                {/* Activity counter + compliance colour */}
                <Box sx={{ textAlign: 'right', minWidth: 80 }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 800,
                    fontFamily: 'ui-monospace, monospace',
                    color: complianceColor(checklistOnly ? p.checklist.compliance : p.wo.compliance) }}>
                    {checklistOnly ? p.checklist.submitted : hideChecklist ? p.wo.completed : p.activity}
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: 'text.disabled',
                    letterSpacing: 0.4, textTransform: 'uppercase', fontWeight: 600 }}>
                    {checklistOnly ? 'checklists' : hideChecklist ? woLabelShort.toLowerCase() + 's' : 'items'}
                  </Typography>
                </Box>
              </Box>
            )
          })}
        </Stack>
      )}
    </Box>
  )
}

// ─── Time slots panel ───────────────────────────────────────────────────────
//
// Compliance bucketed into four shift-style windows. CSS-only "radar"
// implemented as a 4-axis polygon so we don't pull in another chart
// library; readouts beside the polygon show the exact numbers per
// slot. Toggle between WO and Checklist series.

function TimeSlotsPanel({ data, woLabelShort, checklistOnly, hideChecklist }: {
  data: DetailedDashboardData
  woLabelShort: string
  checklistOnly: boolean
  hideChecklist: boolean
}) {
  const [series, setSeries] = useState<'wo' | 'checklist'>('wo')
  const effSeries = checklistOnly ? 'checklist' : hideChecklist ? 'wo' : series
  const slots = data.timeSlots
  const values = slots.map((s) => effSeries === 'wo' ? s.wo.compliance : s.checklist.compliance)
  // Build a polygon from the 4 values (one per slot, clockwise from top).
  // Each axis sits at 0/90/180/270°; value scales 0-100 → radius
  // proportion 0-1. SVG viewBox is 0..200 with centre at 100.
  const radius = 80
  const cx = 100, cy = 100
  const angleAt = (i: number) => (-Math.PI / 2) + (i * Math.PI / 2)
  const pointFor = (i: number, v: number) => {
    const a = angleAt(i)
    const r = (Math.max(0, Math.min(100, v)) / 100) * radius
    return [cx + Math.cos(a) * r, cy + Math.sin(a) * r] as const
  }
  const ringPath = (pct: number) => {
    const r = (pct / 100) * radius
    return [0, 1, 2, 3].map((i) => {
      const a = angleAt(i)
      return `${i === 0 ? 'M' : 'L'} ${cx + Math.cos(a) * r} ${cy + Math.sin(a) * r}`
    }).join(' ') + ' Z'
  }
  const polyPoints = values.map((v, i) => pointFor(i, v).join(',')).join(' ')

  return (
    <Box sx={{
      p: 2.5, borderRadius: 2,
      bgcolor: 'background.paper',
      border: '1px solid', borderColor: 'divider',
      height: '100%',
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 800, textTransform: 'uppercase',
          letterSpacing: '0.08em', color: 'text.secondary' }}>
          Time slot analysis
        </Typography>
        {!checklistOnly && !hideChecklist && (
          <ToggleButtonGroup
            size="small" value={series} exclusive
            onChange={(_, v) => v && setSeries(v)}
          >
            <ToggleButton value="wo">{woLabelShort}</ToggleButton>
            <ToggleButton value="checklist">Chk</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Stack>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Radar polygon (SVG) */}
        <Box sx={{ flexShrink: 0, width: 200, height: 200 }}>
          <svg viewBox="0 0 200 200" width="200" height="200">
            {/* Concentric grid rings */}
            {[25, 50, 75, 100].map((p) => (
              <path key={p} d={ringPath(p)} fill="none"
                stroke="currentColor" strokeOpacity="0.12" strokeWidth="1" />
            ))}
            {/* Axis lines */}
            {[0, 1, 2, 3].map((i) => {
              const [x, y] = pointFor(i, 100)
              return <line key={i} x1={cx} y1={cy} x2={x} y2={y}
                stroke="currentColor" strokeOpacity="0.12" strokeWidth="1" />
            })}
            {/* Data polygon */}
            <polygon points={polyPoints}
              fill="#6366F1" fillOpacity="0.18"
              stroke="#6366F1" strokeWidth="2" />
            {/* Vertex dots — coloured per-value threshold */}
            {values.map((v, i) => {
              const [x, y] = pointFor(i, v)
              return <circle key={i} cx={x} cy={y} r="4"
                fill={complianceColor(v)}
                stroke="white" strokeWidth="1.5" />
            })}
            {/* Slot labels around the rim */}
            {slots.map((s, i) => {
              const [x, y] = pointFor(i, 122)
              const short = s.label.split(' ')[0]
              return (
                <text key={i} x={x} y={y}
                  textAnchor="middle" dominantBaseline="middle"
                  fontSize="10" fontWeight="700" fill="currentColor"
                  opacity="0.65">
                  {short}
                </text>
              )
            })}
          </svg>
        </Box>

        {/* Readouts */}
        <Stack spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
          {slots.map((s) => {
            const bucket = effSeries === 'wo' ? s.wo : s.checklist
            const completedKey = effSeries === 'wo' ? 'completed' : 'submitted'
            return (
              <Box key={s.slot}>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                  <Typography sx={{ fontSize: 11.5, fontWeight: 600,
                    color: 'text.primary' }}>
                    {s.label}
                  </Typography>
                  <Typography sx={{ fontSize: 11, fontFamily: 'ui-monospace, monospace',
                    fontWeight: 700, color: complianceColor(bucket.compliance) }}>
                    {bucket.compliance.toFixed(0)}%
                  </Typography>
                </Stack>
                <Box sx={{ height: 4, mt: 0.4, borderRadius: 99,
                  bgcolor: 'action.hover', overflow: 'hidden' }}>
                  <Box sx={{ height: '100%',
                    width: `${Math.min(100, bucket.compliance)}%`,
                    bgcolor: complianceColor(bucket.compliance),
                    transition: 'width 600ms cubic-bezier(0.32, 0.72, 0, 1)' }} />
                </Box>
                <Typography sx={{ fontSize: 9.5, color: 'text.disabled', mt: 0.25 }}>
                  {(bucket as any)[completedKey]} / {bucket.scheduled} scheduled
                </Typography>
              </Box>
            )
          })}
        </Stack>
      </Box>
    </Box>
  )
}

// ─── Observations panel ─────────────────────────────────────────────────────
//
// Auto-generated findings from the backend — drops, climbs, top
// performers, hotspot locations. Renders as colour-coded callouts so
// the user can scan for what needs attention without parsing charts.

// ─── Tone helpers (shared by Observations / Good Work / Actions) ───────────

const toneColor = (tone: 'red' | 'amber' | 'green' | 'blue') =>
  tone === 'red' ? '#EF4444'
  : tone === 'amber' ? '#F59E0B'
  : tone === 'green' ? '#10B981'
  : '#3B82F6'
const toneBg = (tone: 'red' | 'amber' | 'green' | 'blue') =>
  alpha(toneColor(tone), 0.08)

function CalloutCard({
  tone, title, description, badge,
}: {
  tone: 'red' | 'amber' | 'green' | 'blue'
  title: string
  description: string
  badge: string
}) {
  const c = toneColor(tone)
  return (
    <Box sx={{
      display: 'flex', gap: 1.5,
      p: 1.5, borderRadius: 1.5,
      bgcolor: toneBg(tone),
      border: '1px solid', borderColor: alpha(c, 0.2),
    }}>
      <Box sx={{ width: 6, borderRadius: 99, flexShrink: 0, bgcolor: c }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: c,
            textTransform: 'uppercase', letterSpacing: 0.5,
            fontFamily: 'ui-monospace, monospace' }}>
            {badge}
          </Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>
            {title}
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', lineHeight: 1.55 }}>
          {description}
        </Typography>
      </Box>
    </Box>
  )
}

// ─── Observations panel (red + amber findings only) ────────────────────────
//
// "What needs attention" — drops, hotspots, under-performing groups.
// Green/positive findings get their own panel (Good Work) to mirror
// the sample report's split.

function ObservationsPanel({ data }: { data: DetailedDashboardData }) {
  const items = data.observations.filter((o) => o.tone === 'red' || o.tone === 'amber')

  return (
    <Box sx={{
      p: 2.5, borderRadius: 2,
      bgcolor: 'background.paper',
      border: '1px solid', borderColor: 'divider',
      height: '100%',
    }}>
      <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.12em',
        textTransform: 'uppercase', color: '#EF4444',
        fontFamily: 'ui-monospace, monospace', mb: 0.5 }}>
        // Action required
      </Typography>
      <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 0.5,
        letterSpacing: '-0.01em' }}>
        Key Observations
      </Typography>
      <Typography sx={{ fontSize: 11.5, color: 'text.disabled', mb: 2 }}>
        Auto-detected from compliance, location, time-slot, and people
        metrics in the selected window.
      </Typography>
      {items.length === 0 ? (
        <Typography sx={{ fontSize: 12.5, color: 'text.disabled', py: 3, textAlign: 'center' }}>
          Nothing concerning — no drops, hotspots, or under-performing groups
          detected in this window.
        </Typography>
      ) : (
        <Stack spacing={1.25}>
          {items.map((o, i) => (
            <CalloutCard key={i}
              tone={o.tone}
              title={o.title}
              description={o.description}
              badge={o.tone === 'red' ? 'Action' : 'Watch'}
            />
          ))}
        </Stack>
      )}
    </Box>
  )
}

// ─── Good Work panel (green findings only) ─────────────────────────────────

function GoodWorkPanel({ data }: { data: DetailedDashboardData }) {
  const items = data.observations.filter((o) => o.tone === 'green')

  return (
    <Box sx={{
      p: 2.5, borderRadius: 2,
      bgcolor: 'background.paper',
      border: '1px solid', borderColor: 'divider',
      height: '100%',
    }}>
      <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.12em',
        textTransform: 'uppercase', color: '#10B981',
        fontFamily: 'ui-monospace, monospace', mb: 0.5 }}>
        // Recognition & highlights
      </Typography>
      <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 0.5,
        letterSpacing: '-0.01em' }}>
        Good Work
      </Typography>
      <Typography sx={{ fontSize: 11.5, color: 'text.disabled', mb: 2 }}>
        Top performers and improvements worth recognising from the same window.
      </Typography>
      {items.length === 0 ? (
        <Typography sx={{ fontSize: 12.5, color: 'text.disabled', py: 3, textAlign: 'center' }}>
          No standout wins in this window yet. Stretch the date range or
          loosen the filters to surface more.
        </Typography>
      ) : (
        <Stack spacing={1.25}>
          {items.map((o, i) => (
            <CalloutCard key={i}
              tone="green"
              title={o.title}
              description={o.description}
              badge="Good"
            />
          ))}
        </Stack>
      )}
    </Box>
  )
}

// ─── Recommended Actions panel ─────────────────────────────────────────────
//
// 4-up grid of categorised actions, auto-built from the observations
// list. Mirrors the sample report's "Immediate / This Week / Monthly /
// Client Reporting" buckets so a customer can lift the layout 1:1.

function RecommendedActionsPanel({ data }: { data: DetailedDashboardData }) {
  // Bucket observations by tone, then turn each into a numbered
  // action line. We also append static client-reporting bullets so
  // the 4th box always has content even on a quiet window.
  const reds   = data.observations.filter((o) => o.tone === 'red')
  const ambers = data.observations.filter((o) => o.tone === 'amber')
  const greens = data.observations.filter((o) => o.tone === 'green')
  const wo = data.kpis.workOrders
  const cl = data.kpis.checklists

  const immediate = reds.length > 0
    ? reds.map((o) => o.title).slice(0, 3)
    : ['Compliance is healthy — no urgent escalations this window.']
  const thisWeek = ambers.length > 0
    ? ambers.map((o) => o.title).slice(0, 3)
    : ['No medium-priority watch items this window. Reassess next cycle.']
  const monthly = greens.length > 0
    ? greens.map((o) => o.title).slice(0, 3)
    : ['Maintain the current cadence — consistency is paying off.']
  const clientReporting = [
    `Combined compliance landed at ${(wo.compliance + cl.compliance > 0
      ? ((wo.completed + cl.submitted) / Math.max(1, wo.scheduled + cl.scheduled) * 100)
      : 0).toFixed(1)}% across ${data.range.days} days — share trend chart.`,
    `${data.missed.length} items missed in window — flag for the customer review meeting.`,
    `Top performer led ${data.people[0]?.activity ?? 0} closures — recognise in the report.`,
  ]

  const boxes: Array<{
    tone: 'red' | 'amber' | 'green' | 'blue'
    label: string
    items: string[]
  }> = [
    { tone: 'red',   label: 'Immediate',         items: immediate },
    { tone: 'amber', label: 'This week',         items: thisWeek },
    { tone: 'green', label: 'Monthly',           items: monthly },
    { tone: 'blue',  label: 'Client reporting',  items: clientReporting },
  ]

  return (
    <Box sx={{
      p: 2.5, borderRadius: 2,
      bgcolor: 'background.paper',
      border: '1px solid', borderColor: 'divider',
    }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Box sx={{ fontSize: 18 }}>⚡</Box>
        <Typography sx={{ fontSize: 13, fontWeight: 800, textTransform: 'uppercase',
          letterSpacing: '0.08em', color: 'text.secondary' }}>
          Recommended actions
        </Typography>
      </Stack>
      <Grid container spacing={1.5}>
        {boxes.map((b, i) => {
          const c = toneColor(b.tone)
          return (
            <Grid key={i} size={{ xs: 12, sm: 6 }}>
              <Box sx={{
                bgcolor: toneBg(b.tone),
                border: '1px solid', borderColor: alpha(c, 0.2),
                borderRadius: 1.5, p: 1.75, height: '100%',
              }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6,
                  textTransform: 'uppercase', color: c, mb: 1 }}>
                  {b.label}
                </Typography>
                <Stack spacing={0.75}>
                  {b.items.map((line, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                      <Box sx={{
                        width: 18, height: 18, borderRadius: '50%',
                        bgcolor: alpha(c, 0.18), color: c,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, fontWeight: 800,
                        fontFamily: 'ui-monospace, monospace',
                        flexShrink: 0, mt: 0.1,
                      }}>{idx + 1}</Box>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary',
                        lineHeight: 1.55, flex: 1 }}>
                        {line}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Grid>
          )
        })}
      </Grid>
    </Box>
  )
}

// ─── Group-by-tag panel ──────────────────────────────────────────
//
// Rolls sites up by tag: one card per tag with the sites currently
// assigned to it and the total location count in that tag (which
// comes straight from the tag registry so it doesn't require fetching
// every location tree).
//
// Renders nothing when the org has no tags (empty rollup) — the
// standard KPIs already tell you your site is empty. Once the admin
// adds a tag and assigns it, this panel appears.

function TagGroupsPanel({ sites }: { sites: Site[] }) {
  const tagsQ = useQuery({
    queryKey: ['tags'],
    queryFn: () => api.tags.list(),
    staleTime: 60_000,
  })
  const tags = Array.isArray(tagsQ.data) ? tagsQ.data : []

  // Index sites by tag id for the "which sites are in this tag"
  // preview. Runs client-side against the already-loaded sites list.
  const sitesByTag = useMemo(() => {
    const map = new Map<string, Site[]>()
    for (const s of sites) {
      for (const t of s.tags ?? []) {
        if (!map.has(t.id)) map.set(t.id, [])
        map.get(t.id)!.push(s)
      }
    }
    return map
  }, [sites])

  // Hide the panel entirely when no tags exist — no point in an
  // empty-state card cluttering the dashboard.
  if (tagsQ.isLoading) {
    return (
      <Stack direction="row" alignItems="center" spacing={1.5}
        sx={{ color: 'text.secondary' }}>
        <CircularProgress size={14} />
        <Typography sx={{ fontSize: 12 }}>Loading tag groups…</Typography>
      </Stack>
    )
  }
  if (tags.length === 0) return null

  // Sort by total usage descending — tags with the most assignments
  // read first, so an admin doesn't have to scroll past unused ones.
  const sorted = [...tags].sort((a, b) => {
    const aTotal = a.assigned_site_count + a.assigned_location_count
    const bTotal = b.assigned_site_count + b.assigned_location_count
    return bTotal - aTotal
  })

  return (
    <Box sx={{
      p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider',
      bgcolor: 'background.paper',
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Group by tag</Typography>
        <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>
          {tags.length} tag{tags.length === 1 ? '' : 's'} · click a site to open it
        </Typography>
      </Stack>

      <Grid container spacing={2}>
        {sorted.map((tag) => {
          const sitesInTag = sitesByTag.get(tag.id) ?? []
          const isEmpty = tag.assigned_site_count === 0 && tag.assigned_location_count === 0
          return (
            <Grid key={tag.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Box sx={{
                p: 2, borderRadius: 2, height: '100%',
                border: '1px solid',
                borderColor: isEmpty ? 'divider' : `${tag.color}66`,
                bgcolor: isEmpty ? 'transparent' : `${tag.color}0a`,
                display: 'flex', flexDirection: 'column', gap: 1.25,
                transition: 'border-color 120ms ease, background-color 120ms ease',
              }}>
                {/* Header: colour dot + name + counts */}
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{
                    width: 10, height: 10, borderRadius: 99,
                    bgcolor: tag.color, flexShrink: 0,
                    boxShadow: `0 0 0 3px ${tag.color}22`,
                  }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 800, flex: 1, minWidth: 0,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {tag.name}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1.5}>
                  <MetricPill
                    label={tag.assigned_site_count === 1 ? 'site' : 'sites'}
                    value={tag.assigned_site_count}
                    color={tag.color}
                  />
                  <MetricPill
                    label={tag.assigned_location_count === 1 ? 'location' : 'locations'}
                    value={tag.assigned_location_count}
                    color={tag.color}
                  />
                </Stack>
                {/* Site preview list — top 5 sites in this tag */}
                {sitesInTag.length > 0 && (
                  <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 700,
                      textTransform: 'uppercase', letterSpacing: '0.06em',
                      color: 'text.disabled', mb: 0.5 }}>
                      Sites
                    </Typography>
                    <Stack spacing={0.5}>
                      {sitesInTag.slice(0, 5).map((s) => (
                        <Typography key={s.id} sx={{
                          fontSize: 12, color: 'text.secondary',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          · {s.name}
                        </Typography>
                      ))}
                      {sitesInTag.length > 5 && (
                        <Typography sx={{ fontSize: 11, color: 'text.disabled', fontStyle: 'italic' }}>
                          + {sitesInTag.length - 5} more
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                )}
                {isEmpty && (
                  <Typography sx={{ fontSize: 11.5, color: 'text.disabled', fontStyle: 'italic' }}>
                    Not assigned to any site yet.
                  </Typography>
                )}
              </Box>
            </Grid>
          )
        })}
      </Grid>
    </Box>
  )
}

function MetricPill({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <Stack direction="row" alignItems="baseline" spacing={0.5}
      sx={{
        px: 1, py: 0.5, borderRadius: 1.25,
        bgcolor: `${color}18`,
        border: '1px solid', borderColor: `${color}44`,
      }}>
      <Typography sx={{ fontSize: 15, fontWeight: 800, color, lineHeight: 1 }}>
        {value.toLocaleString()}
      </Typography>
      <Typography sx={{ fontSize: 10.5, color, fontWeight: 600, opacity: 0.85 }}>
        {label}
      </Typography>
    </Stack>
  )
}
